import { ForkMode, SyncMode, VersionControlMode } from '@prophecy/graphql-types/enums';
import { QueryKey, UseQueryOptions } from '@tanstack/react-query';
import { TadaDocumentNode } from 'gql.tada';

import { ReactQueryError, useGraphQlQuery } from '../../data/util';
import { GitQueryKeys } from '../queries/GitQueryKeys';
import { ProjectGitStatusQuery } from '../queries/project';
import { SYNC_STATUS } from './types';

export const useGitStatus = (projectId: string) => {
  const { data, isInitialLoading } = useGraphQlQuery(
    [...GitQueryKeys.ExternalGitStatus],
    ProjectGitStatusQuery,
    { enabled: Boolean(projectId), refetchOnMount: false },
    { projectId }
  );

  const projectInfo = data?.Project;
  const versionControlMode = projectInfo?.versionControlMode;
  const forkMode = projectInfo?.forkMode;
  const syncMode = projectInfo?.syncMode;
  const externalOriginUri = projectInfo?.externalOriginUri;
  const externalSyncStatus = projectInfo?.externalSyncStatus;

  const externalSyncStateStatus = externalSyncStatus?.state;

  const savedGitCreds = projectInfo?.savedGitCredsStatus;

  const isExternal = syncMode === SyncMode.External;
  const isMulti = forkMode === ForkMode.Multi;

  const isLinked = externalSyncStateStatus === SYNC_STATUS.LINKED;

  const deletedUserCreds = isLinked && !savedGitCreds;
  const isLinkedWithCreds = isLinked && savedGitCreds;
  const shouldLinkGitToProceed = !isLinked || !savedGitCreds;
  const isGitLinked = !isInitialLoading && !shouldLinkGitToProceed;
  const currentProjectStatus = projectInfo?.currentProjectStatus;
  return {
    forkMode,
    syncMode,
    externalOriginUri,
    externalSyncStatus,
    isMulti,
    isExternal,
    deletedUserCreds,
    isLinkedWithCreds,
    savedGitCreds,
    shouldLinkGitToProceed,
    isLoading: isInitialLoading,
    isSimpleVersionControl: versionControlMode === VersionControlMode.Simple,
    isGitLinked,
    currentProjectStatus
  };
};
export type UseGitStatusValue = ReturnType<typeof useGitStatus>;
export const useGitGraphQlQuery = <
  TData = unknown,
  V extends { projectId: string } = { projectId: string },
  TQueryFnData = unknown,
  TError = ReactQueryError
>(
  key: QueryKey,
  query: TadaDocumentNode<TData, V> | string,
  queryOptions: UseQueryOptions<TQueryFnData, TError, TData, QueryKey> = {},
  variables: V
) => {
  const { isGitLinked } = useGitStatus(variables.projectId);

  const output = useGraphQlQuery(
    key,
    query,
    { ...queryOptions, enabled: queryOptions.enabled !== false && isGitLinked },
    variables
  );

  return { ...output, data: isGitLinked ? output.data : undefined };
};
